import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import maplibregl from 'maplibre-gl';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { obterConfiguracao } from '@/config/runtime';
import { useI18n } from '@/i18n/I18nProvider';
import { subordinarBasemap } from '@/map/basemap';
import { CENTRO_INICIAL, ESTILO_MINIMO, ZOOM_INICIAL } from '@/map/estilo-minimo';
import { cabecalhosDeCredencial } from '@/services/geo-engine/client';
import 'maplibre-gl/dist/maplibre-gl.css';
const ContextoDoMapa = createContext(null);
/** Instância do MapLibre já carregada, ou `null` enquanto o estilo não terminou de carregar. */
export function useMapa() {
    return useContext(ContextoDoMapa);
}
/** Cria a instância do MapLibre e a disponibiliza às demais partes da aplicação. */
export function Mapa({ children }) {
    const container = useRef(null);
    const [mapa, setMapa] = useState(null);
    const { mapStyleUrl } = obterConfiguracao();
    const { t } = useI18n();
    useEffect(() => {
        if (container.current === null) {
            return undefined;
        }
        const instancia = new maplibregl.Map({
            container: container.current,
            style: mapStyleUrl ?? ESTILO_MINIMO,
            center: CENTRO_INICIAL,
            zoom: ZOOM_INICIAL,
            attributionControl: { compact: true },
            // Os tiles MVT são buscados pelo próprio MapLibre: a credencial vem do cliente da Geo-Engine
            // e só acompanha URLs da API configurada, nunca as do mapa-base.
            transformRequest: (url) => {
                const cabecalhos = cabecalhosDeCredencial(url);
                return cabecalhos === null ? { url } : { url, headers: cabecalhos };
            },
            // Navegação espacial restrita a pan/zoom: o mapa é sempre 2D, sem câmera inclinada.
            pitchWithRotate: false,
            dragRotate: false,
            touchPitch: false,
        });
        instancia.addControl(new maplibregl.NavigationControl({ visualizePitch: false, showCompass: false }), 'bottom-left');
        instancia.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');
        // Observa mudanças de dimensão no container e realinha a viewport do
        // MapLibre. Garante que o painel recolhido (e qualquer outra mudança
        // efetiva de layout) atualize a área visível sem mover a câmera.
        const observer = new ResizeObserver(() => {
            instancia.resize();
        });
        observer.observe(container.current);
        // As camadas só podem ser adicionadas após o estilo carregar; o contexto publica a instância aí.
        instancia.on('load', () => {
            // Só o mapa-base externo precisa ser subordinado; o fundo neutro de contingência já é discreto.
            if (mapStyleUrl !== null) {
                subordinarBasemap(instancia);
            }
            setMapa(instancia);
        });
        return () => {
            observer.disconnect();
            setMapa(null);
            instancia.remove();
        };
    }, [mapStyleUrl]);
    return (_jsxs("div", { className: "mapa", role: "region", "aria-label": t('mapa.regiao'), children: [_jsx("div", { className: "mapa__tela", ref: container, "data-testid": "mapa-tela" }), mapStyleUrl === null ? (_jsxs("p", { className: "aviso-do-mapa", children: [t('mapa.avisoSemBasemapAntes'), _jsx("code", { children: "VIEWER_MAP_STYLE_URL" }), t('mapa.avisoSemBasemapDepois')] })) : null, _jsx(ContextoDoMapa.Provider, { value: mapa, children: children })] }));
}
